/**
 * ConfirmChangeModal Component
 *
 * Modal for confirming platform configuration changes with required reason.
 */

import { useState } from 'react';
import type { PlatformConfig } from '../hooks';

interface ConfirmChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentConfig: PlatformConfig | null;
  newConfig: PlatformConfig;
  onConfirm: (reason: string) => Promise<void>;
}

export const ConfirmChangeModal = ({
  isOpen,
  onClose,
  currentConfig,
  newConfig,
  onConfirm,
}: ConfirmChangeModalProps) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !currentConfig) return null;

  const handleSubmit = async () => {
    if (!reason.trim()) return;

    setIsSubmitting(true);
    try {
      await onConfirm(reason);
      setReason('');
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate changes
  const changes: { field: string; oldValue: string; newValue: string }[] = [];

  // Platform commission changes
  if (currentConfig.fees.platformCommission.value !== newConfig.fees.platformCommission.value) {
    changes.push({
      field: 'Platform Commission (10%)',
      oldValue: `${currentConfig.fees.platformCommission.value}%`,
      newValue: `${newConfig.fees.platformCommission.value}%`,
    });
  }

  // Payout changes
  if (currentConfig.payouts.minimumThreshold !== newConfig.payouts.minimumThreshold) {
    changes.push({
      field: 'Minimum Payout',
      oldValue: `$${currentConfig.payouts.minimumThreshold}`,
      newValue: `$${newConfig.payouts.minimumThreshold}`,
    });
  }
  if (currentConfig.payouts.wireTransferFee !== newConfig.payouts.wireTransferFee) {
    changes.push({
      field: 'Wire Transfer Fee',
      oldValue: `$${currentConfig.payouts.wireTransferFee}`,
      newValue: `$${newConfig.payouts.wireTransferFee}`,
    });
  }
  if (currentConfig.payouts.whishMoneyFee !== newConfig.payouts.whishMoneyFee) {
    changes.push({
      field: 'Whish Money Fee',
      oldValue: `$${currentConfig.payouts.whishMoneyFee}`,
      newValue: `$${newConfig.payouts.whishMoneyFee}`,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <svg
                className="w-6 h-6 text-amber-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold">Confirm Configuration Changes</h2>
              <p className="text-sm text-gray-500">
                These changes will affect all future bookings
              </p>
            </div>
          </div>

          {/* Changes summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Changes to apply:</h3>
            <div className="space-y-2">
              {changes.map((change, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{change.field}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-red-500 line-through">{change.oldValue}</span>
                    <span className="text-gray-400">→</span>
                    <span className="text-green-600 font-medium">{change.newValue}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reason input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for change <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why these changes are being made..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              rows={3}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be recorded in the configuration history for audit purposes.
            </p>
          </div>

          {/* Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-800">
              <strong>Important:</strong> These changes will apply to all new bookings
              immediately after saving. Existing bookings will not be affected.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!reason.trim() || isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Confirm Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmChangeModal;
